import './assets/main.css'
import './assets/vuetify-fixes.css'
import './assets/tutlo-colors.css'
import { aliases, mdi } from 'vuetify/iconsets/mdi-svg'
import { mdiCalendarCheck, mdiCalendarPlus, mdiArrowLeft, mdiGoogle, mdiMicrosoftOutlook, mdiAlertCircle, mdiCheckCircle, mdiCalendar, mdiAccount, mdiEmail, mdiDownload, mdiClockOutline, mdiChevronDown, mdiInformationOutline, mdiEmailOutline, mdiCalendarOutline, mdiCalendarCheckOutline, mdiCalendarPlusOutline, mdiHome, mdiFormTextbox, mdiLinkVariant, mdiHelpCircle, mdiFormatListBulleted, mdiFileQuestionOutline, mdiAccountEdit } from '@mdi/js'
import 'vuetify/styles'

import { createApp } from 'vue'
import { createPinia } from 'pinia'
import { createVuetify } from 'vuetify'
import { VDateInput } from 'vuetify/labs/VDateInput'
import * as directives from 'vuetify/directives'
import { pl } from 'vuetify/locale'


import App from './App.vue'
import router from './router'

// Tutlo Color Palette (from Figma design spec)
const tutloColors = {
  // Primary - Tutlo Yellow (Main brand color)
  primary: {
    50: '#FFF2E5',  // FFFPE3
    100: '#FFE5C9', // FFFC35
    200: '#FEE5A1', // FFE4A1
    300: '#FFD66C', // FFD86C
    400: '#FFC536', // FFC536
    500: '#FDD100', // FDD100 - Main Tutlo Yellow
    600: '#FFC227', // FFC227
    700: '#F5B014', // F5B014
    800: '#FA6319', // FA6319
    900: '#FA6300'  // FA6300
  },
  // Secondary - Tutlo Blue
  secondary: {
    50: '#F0F4FF',  // F0F4FF
    100: '#DBE3FF', // DBE3FF
    200: '#BBC1E6', // BBC1E6
    300: '#A0B8F0', // A0B8F0
    400: '#5FB8F9', // 5FB8F9
    500: '#55CAFF', // 55CAFF - Main Tutlo Blue
    600: '#2B5A0B', // 2B5A0B
    700: '#1F4FCF', // 1F4FCF
    800: '#144A95', // 144A95
    900: '#0C387F'  // 0C387F
  },
  // Warning/Error - Tutlo Red
  warn: {
    50: '#FFF2F2',  // FFF2F2
    100: '#FFCBC8', // FFC8C8
    200: '#FF9B95', // FF9595
    300: '#FF7573', // FF7373
    400: '#F5B95F', // F5B95F
    500: '#E85645', // E85645 - Main Tutlo Red
    600: '#E5BC3C', // E5BC3C
    700: '#C3B020', // C3B020
    800: '#B01919', // B01919
    900: '#800000'  // 800000
  },
  // Supporting colors from Figma
  green: {
    50: '#EEFAEF',  // EEFAEF
    100: '#D2F0D4', // D2F0D4
    200: '#A47994', // A47994
    300: '#9CD95', // 9CD95
    400: '#5CA860', // 5CA860
    500: '#38BC3C', // 38BC3C - Main green
    600: '#38BC3C', // 38BC3C
    700: '#2BAD2F', // 2BAD2F
    800: '#08AE06', // 08AE06
    900: '#032606'  // 032606
  },
  orange: {
    50: '#FFF2E8',  // FFF2E8
    100: '#FFD888', // FFD888
    200: '#FFC369', // FFC369
    300: '#FFAA6C', // FFAA6C
    400: '#FF9141', // FF9141
    500: '#FF7C41', // FF7C41 - Main orange
    600: '#FF7C41', // FF7C41
    700: '#FF8000', // FF8000
    800: '#F9621F', // F9621F
    900: '#F4EC00'  // F4EC00
  },
  focus: {
    50: '#FFF2FE',  // FFF2FE
    100: '#FFC1FE', // FFC1FE
    200: '#FFCOFD', // FFCOFD
    300: '#FF89FC', // FF89FC
    400: '#FFA3FB', // FFA3FB
    500: '#FB98FA', // FB98FA - Main focus/purple
    600: '#FB98FA', // FB98FA
    700: '#D5AFCE', // D5AFCE
    800: '#BA7B84', // BA7B84
    900: '#A90E6A'  // A90E6A
  },
  // Neutral colors
  black: '#333333',      // TUTLO Black
  contrast: {
    dark: '#333333',     // Contrast dark
    light: '#FFFFFF'     // Contrast light
  }
}

// Vuetify theme configuration with corrected Tutlo colors
const vuetify = createVuetify({
  icons: {
    defaultSet: 'mdi',
    aliases: {
      ...aliases,
      calendarCheck: mdiCalendarCheck,
      arrowLeft: mdiArrowLeft,
      calendarPlus: mdiCalendarPlus,
      google: mdiGoogle,
      microsoftOutlook: mdiMicrosoftOutlook,
      alertCircle: mdiAlertCircle,
      checkCircle: mdiCheckCircle,
      calendar: mdiCalendar,
      account: mdiAccount,
      email: mdiEmail,
      download: mdiDownload,
      clockOutline: mdiClockOutline,
      chevronDown: mdiChevronDown,
      informationOutline: mdiInformationOutline,
      emailOutline: mdiEmailOutline,
      calendarOutline: mdiCalendarOutline,
      calendarCheckOutline: mdiCalendarCheckOutline,
      calendarPlusOutline: mdiCalendarPlusOutline,
      home: mdiHome,
      formTextbox: mdiFormTextbox,
      linkVariant: mdiLinkVariant,
      helpCircle: mdiHelpCircle,
      formatListBulleted: mdiFormatListBulleted,
      fileQuestionOutline: mdiFileQuestionOutline,
      accountEdit: mdiAccountEdit,
    },
    sets: {
      mdi,
    },
  },
  locale: {
    locale: 'pl',
    fallback: 'en',
    messages: { pl }
  },
  components: {
    VDateInput,
  },
  directives,
  defaults: {
    // Keep minimal defaults to avoid conflicts
    VBtn: {
      rounded: 'lg', // 10px border radius for all buttons
    }
  },
  display: {
    mobileBreakpoint: 'sm',
    thresholds: {
      xs: 0,
      sm: 600,
      md: 960,
      lg: 1280,
      xl: 1920,
    },
  },
  theme: {
    defaultTheme: 'light',
    themes: {
      light: {
        colors: {
          // PRIMARY = YELLOW (Main brand color)
          primary: tutloColors.primary[500],           // #FDD100
          'primary-lighten-1': tutloColors.primary[400], // #FFC536
          'primary-lighten-2': tutloColors.primary[300], // #FFD66C
          'primary-lighten-3': tutloColors.primary[200], // #FEE5A1
          'primary-lighten-4': tutloColors.primary[100], // #FFE5C9
          'primary-lighten-5': tutloColors.primary[50],  // #FFF2E5
          'primary-darken-1': tutloColors.primary[600],  // #FFC227
          'primary-darken-2': tutloColors.primary[700],  // #F5B014
          'primary-darken-3': tutloColors.primary[800],  // #FA6319
          'primary-darken-4': tutloColors.primary[900],  // #FA6300

          // SECONDARY = BLUE
          secondary: tutloColors.secondary[500],           // #55CAFF
          'secondary-lighten-1': tutloColors.secondary[400], // #5FB8F9
          'secondary-lighten-2': tutloColors.secondary[300], // #A0B8F0
          'secondary-lighten-3': tutloColors.secondary[200], // #BBC1E6
          'secondary-lighten-4': tutloColors.secondary[100], // #DBE3FF
          'secondary-lighten-5': tutloColors.secondary[50],  // #F0F4FF
          'secondary-darken-1': tutloColors.secondary[600],  // #2B5A0B
          'secondary-darken-2': tutloColors.secondary[700],  // #1F4FCF
          'secondary-darken-3': tutloColors.secondary[800],  // #144A95
          'secondary-darken-4': tutloColors.secondary[900],  // #0C387F

          // Keep accent as yellow for compatibility
          accent: tutloColors.primary[500],
          'accent-lighten-1': tutloColors.primary[400],
          'accent-lighten-2': tutloColors.primary[300],
          'accent-darken-1': tutloColors.primary[600],
          'accent-darken-2': tutloColors.primary[700],
          'on-accent': '#000000', // Black text on yellow background

          // Error/Warning colors
          error: tutloColors.warn[500],
          'error-lighten-1': tutloColors.warn[400],
          'error-lighten-2': tutloColors.warn[300],
          'error-darken-1': tutloColors.warn[600],

          warning: tutloColors.primary[600], // Use darker yellow for warnings
          'warning-lighten-1': tutloColors.primary[500],
          'warning-darken-1': tutloColors.primary[700],

          info: tutloColors.secondary[600],  // Use blue for info
          success: tutloColors.green[500],

          // Custom Tutlo colors
          'tutlo-yellow': tutloColors.primary[500],      // Primary brand
          'tutlo-blue': tutloColors.secondary[500],      // Secondary brand
          'tutlo-red': tutloColors.warn[500],
          'tutlo-green': tutloColors.green[500],
          'tutlo-orange': tutloColors.orange[500],
          'tutlo-focus': tutloColors.focus[500],
        },
      },
      dark: {
        colors: {
          // PRIMARY = YELLOW (slightly adjusted for dark theme)
          primary: tutloColors.primary[400],           // Lighter yellow for dark
          'primary-lighten-1': tutloColors.primary[300],
          'primary-lighten-2': tutloColors.primary[200],
          'primary-lighten-3': tutloColors.primary[100],
          'primary-lighten-4': tutloColors.primary[50],
          'primary-darken-1': tutloColors.primary[500],
          'primary-darken-2': tutloColors.primary[600],
          'primary-darken-3': tutloColors.primary[700],
          'primary-darken-4': tutloColors.primary[800],

          // SECONDARY = BLUE (adjusted for dark theme)
          secondary: tutloColors.secondary[400],       // Lighter blue for dark
          'secondary-lighten-1': tutloColors.secondary[300],
          'secondary-lighten-2': tutloColors.secondary[200],
          'secondary-lighten-3': tutloColors.secondary[100],
          'secondary-lighten-4': tutloColors.secondary[50],
          'secondary-darken-1': tutloColors.secondary[500],
          'secondary-darken-2': tutloColors.secondary[600],
          'secondary-darken-3': tutloColors.secondary[700],
          'secondary-darken-4': tutloColors.secondary[800],

          accent: tutloColors.primary[400], // Lighter yellow for dark theme
          'accent-lighten-1': tutloColors.primary[300],
          'accent-lighten-2': tutloColors.primary[200],
          'accent-darken-1': tutloColors.primary[500],
          'accent-darken-2': tutloColors.primary[600],
          'on-accent': '#000000', // Black text on yellow background

          error: tutloColors.warn[400], // Lighter red for dark theme
          'error-lighten-1': tutloColors.warn[300],
          'error-lighten-2': tutloColors.warn[200],
          'error-darken-1': tutloColors.warn[500],

          warning: tutloColors.primary[500],
          'warning-lighten-1': tutloColors.primary[400],
          'warning-darken-1': tutloColors.primary[600],

          info: tutloColors.secondary[500],
          success: tutloColors.green[500],

          // Custom Tutlo colors for dark theme
          'tutlo-yellow': tutloColors.primary[400],
          'tutlo-blue': tutloColors.secondary[400],
          'tutlo-red': tutloColors.warn[400],
          'tutlo-green': tutloColors.green[500],
          'tutlo-orange': tutloColors.orange[500],
          'tutlo-focus': tutloColors.focus[500],
        },
      },
    },
  },
})

const app = createApp(App)

app.use(createPinia())
app.use(router)
app.use(vuetify)

app.mount('#app')
